import { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ChatSidebar({ briefingId, open, onClose, contextSection, contextFinding }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!open || !briefingId) return;
    api.chat.list(briefingId).then((d) => setMessages(d.items || [])).catch(() => {});
  }, [open, briefingId]);

  useEffect(() => {
    if (!open) return;
    if (contextFinding) {
      setInput(`About "${contextFinding.title}" — ${contextFinding.detail}`.slice(0, 500));
    } else {
      setInput("");
    }
  }, [contextFinding, open]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setLoading(true);
    // optimistic append
    setMessages((prev) => [
      ...prev,
      {
        message_id: `temp-${Date.now()}`,
        role: "user",
        content: text,
        context_section: contextSection?.key,
        created_at: new Date().toISOString(),
      },
    ]);
    setInput("");
    try {
      const res = await api.chat.send({
        briefing_id: briefingId,
        message: text,
        context_section: contextSection?.key,
      });
      setMessages((prev) => {
        // replace optimistic with actual
        const withoutTemp = prev.filter((m) => !String(m.message_id).startsWith("temp-"));
        return [...withoutTemp, res.user_message, res.assistant_message];
      });
    } catch (e) {
      toast.error("The agent couldn't respond. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <aside
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl md:w-[420px]"
      data-testid="chat-sidebar"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} strokeWidth={1.8} className="text-[#0052FF]" />
          <div>
            <p className="font-heading text-sm font-medium text-slate-900">Ask the agent</p>
            <p className="text-xs text-slate-500">
              {contextSection ? `Focused on: ${contextSection.title}` : "Whole account · facts only, no guessing"}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900"
          data-testid="chat-close"
        >
          <X size={18} strokeWidth={1.8} />
        </button>
      </div>

      {contextSection && (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            context
          </span>
          <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
            {contextSection.title}
          </span>
        </div>
      )}

      <div ref={scrollerRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
        {messages.length === 0 && !loading && (
          <div className="text-center">
            <p className="mt-8 text-sm text-slate-500">
              {contextSection
                ? <>Try: <em>"Why is this underperforming?"</em> or <em>"What should I do here first?"</em></>
                : <>Try: <em>"What would you pause first?"</em> or <em>"Where's the biggest opportunity?"</em></>}
            </p>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.message_id}
            className={`max-w-[90%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
              m.role === "user"
                ? "ml-auto bg-[#0052FF] text-white"
                : "bg-slate-100 text-slate-800"
            }`}
            data-testid={`chat-msg-${m.role}`}
          >
            {m.content}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-500">
            <span className="loading-dot">·</span>
            <span className="loading-dot">·</span>
            <span className="loading-dot">·</span>
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 p-4">
        <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-white p-2 focus-within:border-[#0052FF] focus-within:ring-2 focus-within:ring-[#0052FF]/20">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder={contextSection ? `Ask about ${contextSection.title}…` : "Ask anything about this account…"}
            className="flex-1 resize-none bg-transparent px-2 py-1 text-sm focus:outline-none"
            data-testid="chat-input"
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-[#0052FF] text-white transition-colors hover:bg-[#0040CC] disabled:bg-slate-200 disabled:text-slate-400"
            data-testid="chat-send"
          >
            <Send size={16} strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </aside>
  );
}
